import { useRef, useState } from 'react'

import { SendHorizontal, Square } from 'lucide-react'


const SUGGESTIONS = [
  'How many open bugs do I have across my repositories?',
  'Which repository has the most unresolved issues?',
  "Find issues assigned to me that haven't been updated in 30 days",
  'Show the open issues in octocat/backend',
]

export function Composer({
  onSend,
  onStop,
  isStreaming,
  showSuggestions,
}: {
  onSend: (value: string) => void
  onStop: () => void
  isStreaming: boolean
  showSuggestions: boolean
}) {
  const [value, setValue] = useState('')
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const submit = () => {
    const trimmed = value.trim()
    if (!trimmed || isStreaming) return
    onSend(trimmed)
    setValue('')
  }

  return (
    <div className="border-t border-line bg-surface px-4 py-3 sm:px-5 sm:py-4">
      {showSuggestions ? (
        <div className="mb-3 flex flex-wrap gap-2">
          {SUGGESTIONS.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => {
                setValue(suggestion)
                inputRef.current?.focus()
              }}
              className="rounded-full bg-raised px-3.5 py-1.5 text-xs text-ink-dim
                transition-colors hover:bg-primary-wash hover:text-primary-bright"
            >
              {suggestion}
            </button>
          ))}
        </div>
      ) : null}

      <form
        className="flex items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        <label className="sr-only" htmlFor="chat-input">
          Ask about your repositories
        </label>
        <textarea
          id="chat-input"
          ref={inputRef}
          rows={1}
          value={value}
          disabled={isStreaming}
          placeholder="Ask about your repositories…"
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            // Enter sends; Shift+Enter is a newline. A multi-line question is rare here,
            // and requiring a modifier to send would slow the common case.
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              submit()
            }
          }}
          className="field max-h-40 min-h-[2.75rem] flex-1 resize-y rounded-3xl px-4 py-3"
        />
        {/* A round button the size of the field's end cap: one target, one icon, the shape
            every messaging composer ends in. The name is on it for anything that reads. */}
        {isStreaming ? (
          <button
            type="button"
            onClick={onStop}
            title="Stop this turn"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-raised
              text-ink-dim transition-colors hover:bg-overlay hover:text-ink"
          >
            <Square aria-hidden="true" className="h-4 w-4" />
            <span className="sr-only">Stop</span>
          </button>
        ) : (
          <button
            type="submit"
            disabled={!value.trim()}
            title="Send"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary
              text-ground transition-colors hover:bg-primary-bright disabled:cursor-not-allowed
              disabled:bg-raised disabled:text-ink-faint"
          >
            <SendHorizontal aria-hidden="true" className="h-[18px] w-[18px]" />
            <span className="sr-only">Send</span>
          </button>
        )}
      </form>
    </div>
  )
}
